import { Component, Input, OnDestroy, OnInit, Signal, TemplateRef } from '@angular/core';
import { debounceTime, map } from 'rxjs/operators';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { VariationValue } from '@harnessio/ff-javascript-client-sdk';
import { DsBreakpoints } from '@levelaccess/design-system';
import { LoaderService } from '../../services/loader.service';
import { WindowService } from '../../services/window.service';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { RequiredSecurities } from '../../../../shared/constants/required-securities';
import { UserAclService } from '../../services/user-acl.service';
import { AclSecurityAdapter } from '../../services/acl.service';
import { AiService } from '../../services/ai.service';
import { NavService } from '../../services/navigation/nav.service';
import { AiChatStateService } from '../../components/ai-chat/ai-chat-state.service';
import { AiChatMobileModalService } from '../../components/ai-chat/ai-chat-mobile-modal.service';
import { UserService } from '../../services/user.service';
import { AppConfigService } from '../../services/app-config.service';
import { MainContentLayoutService } from '../../services/main-content-layout.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
  standalone: false,
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;

  public isLoading: boolean;
  public showSkipToAiChatLink$: Observable<boolean>;
  public isVerticalNavigationEnabled: Signal<boolean>;
  public showAiChatPanel$: Observable<boolean>;
  public showVerticalNavigationPanel: Signal<boolean>;
  public isAiChatExpanded: Signal<boolean>;
  public fillMainContent: Signal<boolean>;

  @Input() public routerOutlet: TemplateRef<any>;

  constructor(
    private loaderService: LoaderService,
    private windowService: WindowService,
    private featureFlagService: FeatureFlagService,
    private userAclService: UserAclService,
    private aiService: AiService,
    private navService: NavService,
    private aiChatStateService: AiChatStateService,
    private userService: UserService,
    private appConfigService: AppConfigService,
    private mainContentLayoutService: MainContentLayoutService,
    // Injected to trigger initialization of mobile modal management
    _aiChatMobileModalService: AiChatMobileModalService,
  ) {
    this.subscriptions = new Subscription();
    this.isLoading = true;
    this.isVerticalNavigationEnabled = toSignal(this.navService.isVerticalNavigationEnabled$);
    this.showAiChatPanel$ = this.aiChatStateService.showAiChatPanel$;
    this.isAiChatExpanded = toSignal(this.aiChatStateService.showExpandedPanel$, { initialValue: false });
    this.showVerticalNavigationPanel = toSignal(
      combineLatest([this.windowService.observeBreakpoint(DsBreakpoints.Large), this.userService.isAuthenticated$]).pipe(
        map(([isBelowLarge, isAuthenticated]: [boolean, boolean]) => !isBelowLarge && isAuthenticated),
      ),
    );
    this.fillMainContent = this.mainContentLayoutService.fillMainContent;
  }

  public ngOnInit(): void {
    this.listenToLoader();
    this.setShowSkipToAiChatLink();
  }

  private setShowSkipToAiChatLink(): void {
    const aiChatFF$: Observable<boolean> = this.featureFlagService
      .variation$(FeatureFlagCollection.aiChat, false)
      .pipe(map((value: VariationValue): boolean => (SharedCommonUtility.notNullish(value) ? Boolean(value) : value)));

    const isAdmin = !this.appConfigService.isTenantEnabled();

    const aiChatPermission$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useFunctionalActions(RequiredSecurities.AI_CHAT_Create_DP.functionalActions)
            .useRequiredSecurity(RequiredSecurities.AI_CHAT_Create_DP, isAdmin)
            .check(),
        ),
      );

    this.showSkipToAiChatLink$ = combineLatest([aiChatFF$, this.aiService.aiEnabledForTenant$, aiChatPermission$]).pipe(
      map((checks: boolean[]): boolean => checks.every((element: boolean) => element)),
    );
  }

  private listenToLoader(): void {
    this.subscriptions.add(
      this.loaderService.loader.pipe(debounceTime(200)).subscribe((loading: boolean): void => {
        this.isLoading = loading;
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
